import React, { useMemo } from 'react';
import { EventType, TrackingEvent, useTracking } from '#core/Tracking';
import useLogger, { Logger } from '#hooks/useLogger';

// These are selected events from https://reactjs.org/docs/events.html
const SUPPORTED_EVENTS = [
  'onFocus',
  'onBlur',
  'onChange',
  'onInput',
  'onInvalid',
  'onReset',
  'onSubmit',
  'onError',
  'onLoad',
  'onClick',
  'onContextMenu',
  'onDoubleClick',
  'onDrag',
  'onDragEnd',
  'onDragEnter',
  'onDragExit',
  'onDragLeave',
  'onDragOver',
  'onDragStart',
  'onDrop',
  'onMouseDown',
  'onMouseEnter',
  'onMouseLeave',
  'onMouseMove',
  'onMouseOut',
  'onMouseOver',
  'onMouseUp',
  'onPointerDown',
  'onPointerMove',
  'onPointerUp',
  'onPointerCancel',
  'onGotPointerCapture',
  'onLostPointerCapture',
  'onPointerEnter',
  'onPointerLeave',
  'onPointerOver',
  'onPointerOut',
  'onSelect',
  'onTouchCancel',
  'onTouchEnd',
  'onTouchMove',
  'onTouchStart',
  'onScroll',
  'onWheel',
  'onToggle',
];

type EventDataCallback = (
  e: React.SyntheticEvent,
) => TrackingEvent | null | undefined | void;

interface WithEventTrackingProps {
  eventData: EventDataCallback;
}

const withEventTrackingCallback = ({
  eventFn,
  eventData,
  trackEvent,
  logger,
}: {
  eventFn: any;
  eventData: EventDataCallback;
  trackEvent: (event: TrackingEvent) => void;
  logger: Logger;
}) => (...args: any) => {
  const result = eventFn(...args);
  if (
    !args[0].nativeEvent ||
    !(args[0].nativeEvent instanceof Event) ||
    result === false
  ) {
    // for some reason this isn't a React event handler or the handler returned false, so ignore tracking events
    return;
  }

  const event = eventData(
    (args[0] as unknown) as React.SyntheticEvent<any, typeof args[0]>,
  );
  if (!event) {
    return;
  }

  try {
    trackEvent(event);
  } catch (error) {
    logger.error(error as EventType.ERROR);
  }
};

const withEventTracking = <T extends {} = {}>(
  Component: React.ComponentType<T>,
): React.FC<T & WithEventTrackingProps> => ({ eventData, ...props }) => {
  const { trackEvent } = useTracking();
  const logger = useLogger();
  const mutatedProps = useMemo(
    () =>
      Object.entries(props).reduce(
        (acc, [eventKey, eventFn]) =>
          SUPPORTED_EVENTS.includes(eventKey) && typeof eventFn === 'function'
            ? {
                ...acc,
                [eventKey]: withEventTrackingCallback({
                  eventFn,
                  eventData,
                  trackEvent,
                  logger,
                }),
              }
            : acc,
        props,
      ),
    [props, eventData, trackEvent, logger],
  ) as T;

  return <Component {...mutatedProps} />;
};

export default withEventTracking;
